/** Arquivo de oportunidades fechadas (SPEC-OPP-021).
 *
 * O board mostra o funil em andamento e só os fechamentos dos últimos 30 dias.
 * Esta é a tela que alcança o resto — e, até ela existir, uma oportunidade
 * perdida há mais de um mês não tinha por onde ser vista.
 *
 * Lista, e não quadro: o que se faz aqui é procurar e conferir, não arrastar.
 * Uma coluna por etapa não ajudaria a responder "o que perdi em março".
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, FalhaDeRede } from "@/lib/http";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";
import { Selecao } from "@/shared/ui/Selecao";
import { useSessao } from "@/features/auth/SessaoProvider";
import { formatarDinheiro } from "@/features/board/formato";

import { buscarArquivo, reabrir, type ItemDoArquivo, type StatusDoArquivo } from "./api";

const ROTULO_DO_STATUS: Record<"won" | "lost", string> = {
  won: "Ganha",
  lost: "Perdida",
};

/** Data e hora do fechamento, dia e mês por extenso curto. Aqui o ano importa
 * sempre — o arquivo é justamente o lugar em que se olha para trás. */
function formatarFechamento(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(data);
}

export function ArquivoDeOportunidades({ aoVoltar }: { aoVoltar: () => void }) {
  const { temPermissao } = useSessao();
  const podeReabrir = temPermissao("opportunity:close");

  const [status, setStatus] = useState<StatusDoArquivo>("all");
  const [busca, setBusca] = useState("");
  const [desde, setDesde] = useState("");
  const [ate, setAte] = useState("");

  const [itens, setItens] = useState<ItemDoArquivo[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [temMais, setTemMais] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [reabrindo, setReabrindo] = useState<string | null>(null);

  /** Número do pedido mais recente. Resposta de pedido antigo é descartada.
   *
   * Sem isto vencia a última a **chegar**, não a última a ser pedida: digitar
   * na busca dispara uma consulta por tecla, e uma resposta lenta de três
   * letras atrás sobrescrevia a lista do que está escrito agora. Pior no
   * "Carregar mais": a página pendente do filtro antigo era **anexada** à lista
   * nova, misturando dois filtros na mesma tela.
   */
  const pedido = useRef(0);

  const carregar = useCallback(
    async (proximoCursor?: string) => {
      const meuPedido = (pedido.current += 1);
      const atual = () => pedido.current === meuPedido;

      setCarregando(true);
      setErro(null);
      try {
        const pagina = await buscarArquivo({
          status,
          busca,
          desde: desde === "" ? undefined : `${desde}T00:00:00Z`,
          // Fim do dia: quem filtra "até 31/03" espera incluir o dia 31
          // inteiro, não parar à meia-noite dele.
          ate: ate === "" ? undefined : `${ate}T23:59:59Z`,
          cursor: proximoCursor,
        });
        if (!atual()) return;

        // Página seguinte acumula; filtro novo recomeça. Sem essa distinção, o
        // "carregar mais" apagaria o que já está na tela.
        setItens((atuais) =>
          proximoCursor === undefined ? pagina.items : [...atuais, ...pagina.items],
        );
        setCursor(pagina.meta.next_cursor);
        setTemMais(pagina.meta.has_more);
      } catch (falha) {
        if (!atual()) return;

        if (falha instanceof ApiError) setErro(falha.message);
        else if (falha instanceof FalhaDeRede)
          setErro("Não conseguimos falar com o servidor. Verifique sua conexão.");
        else setErro("Algo deu errado. Tente de novo.");
      } finally {
        // A busca em andamento continua sendo uma busca em andamento: desligar
        // o "carregando" por causa de uma resposta velha piscaria a lista vazia.
        if (atual()) setCarregando(false);
      }
    },
    [status, busca, desde, ate],
  );

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function aoReabrir(item: ItemDoArquivo) {
    setReabrindo(item.id);
    setErro(null);
    try {
      await reabrir(item.id, item.version);
      // Some da lista na hora: ela voltou para o funil, e é lá que a pessoa
      // continua o trabalho. Recarregar tudo custaria uma ida à rede para
      // exibir o mesmo resultado.
      setItens((atuais) => atuais.filter((outro) => outro.id !== item.id));
    } catch (falha) {
      setErro(
        falha instanceof ApiError
          ? falha.message
          : "Não foi possível reabrir. Tente de novo em instantes.",
      );
    } finally {
      setReabrindo(null);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-[1200px] flex-col gap-6 px-6 py-10">
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <p className="font-texto text-micro uppercase tracking-wide text-texto-tenue">
            Histórico
          </p>
          <h1 className="font-display text-display-md text-texto">Oportunidades fechadas</h1>
        </div>
        <Botao variante="secundario" onClick={aoVoltar}>
          Voltar ao funil
        </Botao>
      </header>

      <section
        aria-label="Filtros"
        data-guia="filtros-do-arquivo"
        className="flex flex-wrap items-end gap-3"
      >
        {/* `Selecao` do design system, e não um `<select>` com classe copiada à
            mão: esta tela era uma das divergências que aquele componente foi
            criado para acabar, e tinha ficado para trás. Ao lado dos `Campo`
            desta mesma linha, a diferença de altura e de rótulo aparecia toda. */}
        <div className="w-[160px]">
          <Selecao
            rotulo="Situação"
            value={status}
            onChange={(evento) => {
              setStatus(evento.target.value as StatusDoArquivo);
            }}
          >
            <option value="all">Todas</option>
            <option value="won">Ganhas</option>
            <option value="lost">Perdidas</option>
          </Selecao>
        </div>

        {/* Os dois campos são **um** filtro, e o `fieldset` é o que diz isso.
            Soltos na linha, "Fechadas de" e "até" liam-se como dois filtros
            independentes — e "até", sozinho, não diz até o quê. Para leitor de
            tela a diferença é maior: a `legend` entra na leitura de cada campo,
            então o segundo deixa de ser anunciado como só "até". */}
        <fieldset className="flex items-end gap-3 border-0 p-0">
          <legend className="sr-only">Período de fechamento</legend>
          <div className="w-[150px]">
            <Campo
              rotulo="Fechadas de"
              type="date"
              value={desde}
              onChange={(evento) => {
                setDesde(evento.target.value);
              }}
            />
          </div>
          <div className="w-[150px]">
            <Campo
              rotulo="Fechadas até"
              type="date"
              value={ate}
              onChange={(evento) => {
                setAte(evento.target.value);
              }}
            />
          </div>
        </fieldset>

        <div className="min-w-[220px] flex-1">
          <Campo
            rotulo="Buscar"
            type="search"
            placeholder="Título ou empresa"
            value={busca}
            onChange={(evento) => {
              setBusca(evento.target.value);
            }}
          />
        </div>
      </section>

      {erro !== null && (
        <div
          role="alert"
          className="rounded-md border border-carmim/40 bg-carmim/10 px-4 py-3 font-texto text-body text-texto"
        >
          {erro}
        </div>
      )}

      {carregando && itens.length === 0 ? (
        <p role="status" className="font-texto text-body text-texto-suave">
          Carregando o histórico…
        </p>
      ) : itens.length === 0 ? (
        <p className="font-texto text-body text-texto-suave">
          Nenhuma oportunidade fechada com esses filtros.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {itens.map((item) => (
            <li
              key={item.id}
              // Grade de colunas fixas a partir de `md`, e não `flex-wrap`.
              //
              // Com o empacotamento anterior, cada linha começava o valor num
              // ponto diferente conforme o comprimento do título — e um
              // histórico existe para comparar valores verticalmente. Alinhar
              // é o que transforma a lista em algo que se lê de cima para
              // baixo em vez de linha a linha. Abaixo de `md` empilha, porque
              // ali não há largura para coluna nenhuma.
              className="flex flex-col gap-2 rounded-md border border-borda bg-superficie px-4 py-3 md:grid md:grid-cols-[minmax(220px,1fr)_7rem_9rem_8rem_auto] md:items-center md:gap-x-6"
            >
              <div className="flex min-w-0 flex-col gap-0.5">
                <p className="font-display text-title text-texto">{item.title}</p>
                <p className="font-texto text-caption text-texto-suave">
                  {item.company_name ?? "Sem empresa"}
                  {item.owner_name !== null && ` · ${item.owner_name}`}
                </p>
                {/* O motivo mora aqui, e não numa coluna própria.

                    Duas razões. Ele só existe nas perdidas, e uma coluna que
                    aparece em algumas linhas empurra as seguintes — na grade,
                    o botão "Reabrir" de uma venda ganha caía no lugar do
                    motivo. E ele não é dado comparável entre linhas como valor
                    e data: é a história deste negócio, e pertence ao bloco que
                    conta qual negócio é.

                    É a informação que faz o arquivo valer a visita: saber o
                    que se perdeu importa menos que saber por quê. */}
                {item.status === "lost" && item.lost_reason_code !== null && (
                  <p
                    className="font-texto text-caption text-texto-tenue"
                    title={item.lost_reason_note ?? undefined}
                  >
                    Motivo: {item.lost_reason_code}
                  </p>
                )}
              </div>

              {/* Sem cor semântica: os tokens do design system reservam âmbar,
                  musgo e carmim para o que **exige ação**, e uma venda já
                  fechada não exige nenhuma. O que distingue as duas aqui é o
                  peso da tipografia, não um verde comemorativo. */}
              <span
                className={[
                  "justify-self-start rounded-sm border border-borda px-2 py-1 font-texto text-caption",
                  item.status === "won" ? "font-medium text-texto" : "text-texto-suave",
                ].join(" ")}
              >
                {ROTULO_DO_STATUS[item.status]}
              </span>

              {/* Alinhado à direita e com `tabular-nums`: dígito sob dígito é o
                  que permite ver de relance qual negócio pesou mais, sem ler
                  número por número. Mesma regra do valor da coluna no board. */}
              <span className="font-dados text-body tabular-nums text-texto md:text-right">
                {formatarDinheiro(item.estimated_value_cents, item.currency)}
              </span>

              <span className="font-dados text-caption tabular-nums text-texto-suave">
                {formatarFechamento(item.closed_at)}
              </span>

              {podeReabrir && (
                <Botao
                  variante="fantasma"
                  carregando={reabrindo === item.id}
                  onClick={() => void aoReabrir(item)}
                >
                  Reabrir
                </Botao>
              )}
            </li>
          ))}
        </ul>
      )}

      {temMais && cursor !== null && (
        <Botao variante="secundario" carregando={carregando} onClick={() => void carregar(cursor)}>
          Carregar mais
        </Botao>
      )}
    </main>
  );
}
